import Papa from 'papaparse';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Warning as WarningIcon } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

import ErrorView from '../../../common/views/ErrorView';
import WrappedTableCell from '../../../common/WrappedTableCell';

const StyledDiv = styled.div`
  /* make sure the div only occupies the available width and height
    without extending the parent width and height */
  width: 0;
  height: 0;
  min-width: 100%;
  min-height: 100%;

  &.no-data {
    /* if no data table, make this div scrollable */
    overflow: auto;
  }

  .hidden {
    display: none;
  }
  .error-content {
    height: 100%;
  }

  .MuiTableContainer-root {
    height: 100%;
  }

  .MuiTableHead-root .MuiTableCell-root {
    padding-top: 8px;
    padding-bottom: 8px;
    background-color: ${props => props.theme.palette.grey[200]};

    &:not(:last-child) {
      border-right: 1px solid ${props => props.theme.palette.grey[300]};
    }
  }

  .MuiTableBody-root .table-row {
    &:nth-child(even) .MuiTableCell-root {
      background-color: ${props => props.theme.palette.grey[50]};
    }

    .MuiTableCell-root {
      padding-top: 8px;
      padding-bottom: 8px;

      &:not(:last-child) {
        border-right: 1px solid ${props => props.theme.palette.grey[200]};
      }
    }
  }
`;

const defaultErrorMessage = 'Failed to load CSV file.';

interface CSVData {
  [key: string]: any;
}

export interface CSVViewerProps {
  fileUrl: string;
  className?: string;
}

const CSVViewer: React.FC<CSVViewerProps> = props => {
  const { fileUrl, className } = props;
  const [rows, setRows] = useState<Papa.ParseResult<CSVData>>();
  const [error, setError] = useState<Papa.ParseError>();
  const [isLoading, setIsLoading] = useState(true);
  const classes = ['csv-viewer'];
  className && classes.push(className);
  !rows && classes.push('no-data');

  useEffect(() => {
    Papa.parse<CSVData>(fileUrl, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: data => {
        setRows(data);
        setError(undefined);
        setIsLoading(false);
      },
      error: error => {
        setError(error);
        setIsLoading(false);
      }
    });
  }, [fileUrl]);

  return (
    <StyledDiv className={classes.join(' ')}>
      {rows ? (
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {rows.meta.fields?.map((colummName, columnIndex) => (
                  <TableCell key={columnIndex}>{colummName}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {/* always render loading and error rows so that
                the alternating css table row colors are consistent */}
              <TableRow className={isLoading ? undefined : 'hidden'}>
                <TableCell padding='none' colSpan={rows.meta.fields?.length}>
                  <LinearProgress />
                </TableCell>
              </TableRow>

              <TableRow className={error ? undefined : 'hidden'}>
                <TableCell padding='none' colSpan={rows.meta.fields?.length}>
                  <Alert severity='error'>
                    {error?.message || defaultErrorMessage}
                  </Alert>
                </TableCell>
              </TableRow>

              {rows.data.map((row, rowIndex) => (
                <TableRow key={rowIndex} className='table-row'>
                  {rows.meta.fields?.map((columnName, columnIndex) => (
                    <WrappedTableCell key={`cell-${rowIndex}-${columnIndex}`}>
                      {row[columnName]}
                    </WrappedTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <ErrorView
          className='error-content'
          title={isLoading ? 'Loading' : 'CSV Error'}
          description={
            isLoading ? 'Loading CSV...' : error?.message || defaultErrorMessage
          }
          icon={
            isLoading ? (
              <CircularProgress size={64} color='primary' />
            ) : (
              <WarningIcon color='disabled' fontSize='inherit' />
            )
          }
        />
      )}
    </StyledDiv>
  );
};

export default CSVViewer;
